import { Formik } from "formik";
import * as React from "react";
import * as yup from "yup";
import styled from "styled-components";
import * as _ from "lodash";
import { toast } from "react-toastify";
import { useMappedState } from "redux-react-hook";

import { Button } from "src/library/components/Button";
import {
  Form,
  IFormField,
  IFormOption
} from "src/library/components/Forms";
import { ButtonLink } from "src/library/components/Link";
import Grid from "src/library/components/Hedron";
import CustomerUserInfoSidebar from "src/core/customers/components/CustomerUserInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  UpdateUserParams,
  IUser,
  useUserApi
} from "src/core/users/api/users.api";
import { useUser } from "src/core/users/hooks";
import { PageProps } from "src/pages/Router/routes";
import {
  IRole,
  useRolesApi
} from "src/core/shared/api/roles.api";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { SiteSettings } from "src/core/shared/store/global/global.reducer";
import { IStore } from "src/core/shared/store";
import { e164PhoneRegex } from "src/utils/validation/regex";

type UrlParams = {
  customerId: string;
  userId: string;
};

const updateUserForm: (user: IUser, roles: IRole[], canChangeRole?: boolean, subdomains?: SiteSettings["sso"]["subdomains"]) => IFormField[] = (user, roles, canChangeRole, subdomains) => {
  const fields: IFormField[] = [
    {
      type: "input",
      name: "firstName",
      label: "First Name",
      placeholder: "First Name",
      value: user.firstName,
      validation: yup
        .string()
        .min(1)
        .required("First Name is required")
    },
    {
      type: "input",
      name: "lastName",
      label: "Last Name",
      placeholder: "Last Name",
      value: user.lastName,
      validation: yup
        .string()
        .min(1)
        .required("Last Name is required")
    },
    {
      type: "input",
      name: "email",
      label: "Email",
      disabled: true,
      placeholder: "Email",
      value: user.email,
      validation: yup
        .string()
        .email("Please enter a valid email address")
        .required("Email is required")
    },
    {
      type: "input",
      name: "phoneNumber",
      label: "Phone Number",
      placeholder: "Phone Number",
      value: user.phoneNumber,
      validation:
        yup.string().when("mFAMethod", {
          is: "phoneNumber",
          then: yup.string().matches(e164PhoneRegex, "Please enter a valid UK telephone number starting with +44").required("Phone Number is required"),
          otherwise: yup.string().test("optional-phone", "Please enter a valid UK telephone number starting with +44", function (value) {
            if (!value || value.length === 0) {
              return true; // Allow empty values when not required
            }

            return e164PhoneRegex.test(value);
          })
        })
    },
    {
      type: "radio",
      name: "mFAMethod",
      label: "Multi-Factor Authentication",
      value: user.mFAMethod,
      options: [
        {
          text: "Email - code sent by email, requires an email address",
          value: "email",
          checked: user.mFAMethod === "email" ? true : false
        },
        {
          text: "Telephone - code sent by SMS, requires a phone number",
          value: "phoneNumber",
          checked: user.mFAMethod === "phoneNumber" ? true : false
        },
        {
          text: "Disabled - MFA will not be enabled",
          value: "disabled",
          checked: user.mFAMethod === "disabled" ? true : false
        }
      ]
    },
    {
      type: "checkbox",
      name: "passwordAuthDisabled",
      label: "Disable password login - Users will need to use SSO to login",
      options: [
        {
          text: "Disable Password Login",
          value: "true",
          checked: user.passwordAuthDisabled
        }
      ]
    }
  ];

  if (subdomains?.length) {
    fields.push({
      type: "select",
      name: "useSubdomain",
      label: "Use Subdomain",
      value: user.useSubdomain,
      options: subdomains.map(
        (subdomain): IFormOption => {
          return {
            value: subdomain.name,
            text: subdomain.name
          };
        }
      )
    });
  }

  if (canChangeRole) {
    fields.push({
      type: "select",
      name: "roleId",
      label: "Role",
      placeholder: "Role",
      value: user.roleId.toString(),
      options: _.map(roles, role => ({
        text: `${role.name}`,
        value: role.id.toString()
      })),
      validation: yup
        .string()
        .min(1)
        .required("Role is required")
    });
  }

  return fields;
};

/**
 * Main "EditUser" View
 *
 * @returns
 */
const EditCustomerUserPage: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = props => {
  const { terminology, match } = props;
  const customerId = match ? parseInt(match.params.customerId, 0) : 0;
  const userId = match ? parseInt(match.params.userId, 0) : 0;
  const [ { isLoading: isLoadingUser, user }, { updateUser: setUser } ] = useUser(userId);
  const [ updateUserResponse, updateUser ] = useUserApi("UPDATE_USER");
  const [ getUserResponse, getUser ] = useUserApi("GET_USER");
  const formRef = React.useRef<Formik>(null);
  const [ rolesResponse, listRoles ] = useRolesApi("LIST_ROLES");
  const checkPermission = useCheckUserPermission();
  const siteSettings = useMappedState((store: IStore) => store.global.settings?.siteSettings);

  React.useEffect(() => {
    listRoles({ filters: { type: "customer" } });
  }, [ listRoles ]);

  const selectedRole = React.useMemo(() => {
    return rolesResponse.data?.find(role => role.id === user?.roleId) || null;
  }, [ rolesResponse.data, user?.roleId ]);

  // on mount
  React.useEffect(() => {
    if (props.match) {
      getUser({ id: parseInt(props.match.params.userId) });
    }
  }, [ getUser ]);

  // handles getting the current user
  // if not already in context
  React.useEffect(() => {
    if (getUserResponse.data) {
      setUser(getUserResponse.data);
      getUserResponse.data = null;
    }
  }, [ getUserResponse, setUser ]);

  React.useEffect(() => {
    // On Success reset the form
    if (updateUserResponse.data) {
      props.history.goBack();
    } else if (updateUserResponse.error) {
      toast.error(updateUserResponse.error.message);
    }
  }, [ props.history, updateUserResponse ]);

  React.useEffect(() => {
    if (rolesResponse.error) {
      toast.error(rolesResponse.error.message);
    }
  }, [ rolesResponse.error ]);

  const submitForm = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const submitting = updateUserResponse.pending;

  return (
    <Layout {...props}>
      <MainContentWrapper>
        <FormWrapper>
          {user && !getUserResponse.pending && (
            <Form
              key={`form-${user.id}`}
              ref={formRef}
              error={updateUserResponse.error ? updateUserResponse.error.message : null}
              submitting={updateUserResponse.pending}
              fields={updateUserForm(user, rolesResponse.data || [], checkPermission("amend-role-permissions"), siteSettings?.sso.subdomains)}
              onSubmit={updatedUser => {
                const { passwordAuthDisabled, ...updatedUserRest } = updatedUser;

                updatedUserRest[ "passwordAuthDisabled" ] = passwordAuthDisabled.includes("true");
                updatedUserRest[ "mFAMethod" ] = (updatedUser.mFAMethod as string);
                updateUser(Object.assign(user, updatedUserRest) as UpdateUserParams);
              }}
            />
          )}
        </FormWrapper>
      </MainContentWrapper>

      <InfoBarWrapper>
        <CustomerUserInfoSidebar
          terminology={terminology}
          role={selectedRole}
          primaryAction={(
            <ButtonLink
              $buttonType="secondary"
              to={`/admin/customers/${customerId}/users`}
            >
              Cancel
            </ButtonLink>
          )}
          secondaryAction={(
            <Button
              loading={submitting}
              onClick={submitForm}
            >
              Save
            </Button>
          )}
          isLoading={isLoadingUser}
          data={user}
        />
      </InfoBarWrapper>
    </Layout>
  );
};

const FormWrapper = styled(Grid.Box)`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  form {
    width: 400px;
  }
`;

export default EditCustomerUserPage;
